<template>
  <div class="editor-wrapper" :class="{ ...inputClasses }">
    <Editor
      ref="editor"
      v-model="inputValue"
      class="editor"
      :init="{
        images_upload_handler: imageUploadCallback,
        placeholder,
        language,
        menubar,
        plugins,
        paste_block_drop: true,
        paste_data_images: true,
        paste_as_text: true,
        height,
        max_height: maxHeight,
        min_height: minHeight,
        toolbar_mode: toolbarMode,
        statusbar,
        contextmenu,
        image_upload_handler: fetchImageHandler,
        file_picker_callback: fetchImage,
        fixed_toolbar_container: fixedToolbarContainer,
        init_instance_callback: initInstanceCallback,
        file_picker_types: 'image',
        image_title: true,
        external_plugins: {
          formula: '/js/tinymce/plugins/tinymce-formula/plugin.js',
        },
        codesample_languages,
      }"
      :disabled="disabled"
      :toolbar="toolbarSettings"
      @focus="focusHandler"
      @blur="blurHandler"
    />
    <!--    <label class="editor__label" :for="name">-->
    <!--      <span> {{ label }} </span>-->
    <!--      <span class="required-indicator" v-if="required">*</span>-->
    <!--    </label>-->
    <div v-if="counterIsVisible" class="s-counter">
      {{ cleanText.length }} /{{ maxCounterValue }}
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, reactive, computed, toRefs } from "vue";
import Editor from "@tinymce/tinymce-vue";
const emit = defineEmits([
  "update:modelValue",
  "input",
  "focusEvent",
  "blurEvent",
]);
const props = defineProps({
  counterIsVisible: {
    type: Boolean,
    default: false,
  },
  imageUploadCallback: {
    type: Function,
    default: () => {},
  },
  maxCounterValue: {
    type: Number,
    default: 0,
  },
  language: {
    type: String,
    default: "ru",
  },
  // name: {
  //   type: String,
  //   default: ''
  // },
  // required: {
  //   type: Boolean,
  //   dafault: false
  // },
  // label: {
  //   type: String,
  //   default: ''
  // },
  toolbarSettings: {
    type: String,
    default:
      "undo redo | codesample | bold italic underline strikethrough | formula | link image code | formatselect | alignleft aligncenter alignright alignjustify | outdent indent | numlist bullist checklist | forecolor backcolor casechange permanentpen formatpainter removeformat | charmap emoticons | table tabledelete | tableprops tablerowprops tablecellprops | tableinsertrowbefore tableinsertrowafter tabledeleterow | tableinsertcolbefore tableinsertcolafter tabledeletecol",
  },
  menubar: {
    type: Boolean,
    default: false,
  },
  statusbar: {
    type: Boolean,
    default: false,
  },
  disabled: {
    type: Boolean,
    default: false,
  },
  plugins: {
    type: String,
    default:
      "formula paste advlist codesample lists checklist pagebreak anchor table autoresize wordcount code link charmap image",
  },
  fixedToolbarContainer: {
    type: String,
    default: "",
  },
  contextmenu: {
    type: String,
    default: "",
  },
  toolbarMode: {
    type: String,
    default: "sliding",
  },
  placeholder: {
    type: String,
    default: "",
  },
  // attachmentUrl: {
  //   type: String,
  //   default: "/attachments",
  // },
  height: {
    type: Number,
    default: 350,
  },
  maxHeight: {
    type: Number,
    default: Math.max(window.innerHeight - 100, 300),
  },
  hasErrors: {
    type: Boolean,
    default: false,
  },
  modelValue: {
    type: String,
    default: "",
  },
  minHeight: {
    type: Number,
    default: 350,
  },
});
const {
  counterIsVisible,
  maxCounterValue,
  language,
  toolbarSettings,
  imageUploadCallback,
  menubar,
  statusbar,
  disabled,
  plugins,
  fixedToolbarContainer,
  contextmenu,
  toolbarMode,
  placeholder,
  hasErrors,
  // attachmentUrl,
  height,
  maxHeight,
  modelValue,
  minHeight,
} = toRefs(props);
const editor = ref<any>(null);
const cleanText = ref<string>("");
const isFocused = ref<boolean>(false);
const codesample_languages = reactive<{ text: string; value: string }[]>([
  { text: "HTML/XML", value: "markup" },
  { text: "JavaScript", value: "javascript" },
  { text: "CSS", value: "css" },
  { text: "PHP", value: "php" },
  { text: "Ruby", value: "ruby" },
  { text: "Python", value: "python" },
  { text: "Java", value: "java" },
  { text: "C", value: "c" },
  { text: "C#", value: "csharp" },
  { text: "C++", value: "cpp" },
]);
const inputValue = computed({
  get() {
    return modelValue.value;
  },
  set(val) {
    emit("update:modelValue", val);
    emit("input", val);
  },
});
const inputClasses = computed(() => {
  return {
    "editor-wrapper--status-focused": isFocused.value,
    "editor-wrapper--status-disabled": disabled.value,
    "editor-wrapper--has-value": inputValue.value,
    "editor-wrapper--has-errors": hasErrors.value,
  };
});

function initInstanceCallback(editor: any) {
  cleanText.value = editor.getContent({ format: "text" });
}
// function imageUploadCallback(blobInfo, success, failure, progress) {
// return new Promise((resolve, reject) => {
//   const formData = new FormData()
//   formData.append(
//       'attachment[file]',
//       blobInfo.blob(),
//       blobInfo.filename()
//   )
//   formData.append(
//       'attachment[client_id]',
//       this.$root?.user?.sub
//   )
//   this.$axios.post(attachmentUrl, formData, {}).then((response) => {
//     success(response.data.data.url)
//     resolve(response.data.data.url)
//     this.$emit('add-attachment', response.data.data)
//   })
// });
// }
function focusHandler() {
  isFocused.value = true;
  emit("focusEvent");
}
function blurHandler() {
  isFocused.value = false;
  emit("blurEvent");
}
function fetchImageHandler() {}
function fetchImage(cb: Function) {
  const input = document.createElement("input");
  input.setAttribute("type", "file");
  input.setAttribute("accept", "image/*");

  /*
    Note: In modern browsers input[type="file"] is functional without
    even adding it to the DOM, but that might not be the case in some older
    or quirky browsers like IE, so you might want to add it to the DOM
    just in case, and visually hide it. And do not forget do remove it
    once you do not need it anymore.
  */
  const blobCache = editor.value.getEditor().editorUpload.blobCache;
  input.onchange = function (e: Event) {
    const { files } = e.target as HTMLInputElement;
    if (files === null) return;
    const file = files[0];
    const reader: any = new FileReader();
    // const reader: FileReader = new FileReader();
    // const readerResult: string | ArrayBuffer | null = reader.result
    reader.onload = function () {
      /*
        Note: Now we need to register the blob in TinyMCEs image blob
        registry. In the next release this part hopefully won't be
        necessary, as we are looking to handle it internally.
      */

      const id = "blobid" + new Date().getTime();
      // if (readerResult === null) return;
      const base64 = reader.result.split(",")[1];
      const blobInfo = blobCache.create(id, file, base64);
      blobCache.add(blobInfo);

      /* call the callback and populate the Title field with the file name */
      cb(blobInfo.blobUri(), { title: file.name });
    };
    reader.readAsDataURL(file);
  };

  input.click();
}
</script>

<style lang="scss" scoped>
// .required-indicator {
//   color: #ff0a47;
// }
.editor-wrapper {
  position: relative;
  border: 1px solid var(--stroke-select);
  border-radius: var(--border-radius);
  &:hover {
    border: 1px solid var(--color-icon-primary);
    outline: var(--border-hover-color) solid 1px;
  }
  &--status-focused {
    border-color: var(--border-focus-color);
    outline: var(--border-focus-color) solid 1px;
    &:hover {
      border-color: var(--border-focus-color);
      outline: var(--border-focus-color) solid 1px;
    }
  }
  &--has-errors {
    border: 1px solid var(--error-color);
    outline: none;
  }
  .s-counter {
    position: absolute;
    bottom: 16px;
    right: 16px;
    font-size: 14px;
    line-height: 20px;
    color: var(--color-text-tertiary);
    font-weight: 400;
  }
}
//.editor {
//  &__label {
//    top: 8px;
//    pointer-events: none;
//    position: absolute;
//    margin-bottom: 0;
//    transform-origin: left;
//    color: #BABCC2;
//    transition: all 0.2s ease-out;
//  }
//}
</style>
